import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, CalendarCheck, UserRound, BellRing, ShieldCheck, MailWarning, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { oapi } from '@/lib/officialsApi';
import { OfficialsHeader, OfficialsLoader } from '@/components/officials/OfficialsHeader';
import { PortalAssignments } from '@/components/officials/portal/PortalAssignments';
import { AvailabilityEditor } from '@/components/officials/portal/AvailabilityEditor';
import { PortalProfile } from '@/components/officials/portal/PortalProfile';
import { MyEarnings } from '@/components/officials/portal/MyEarnings';

const TABS = [
  { key: 'assignments', label: 'My games', icon: ClipboardList },
  { key: 'availability', label: 'Availability', icon: CalendarCheck },
  { key: 'earnings', label: 'Earnings', icon: Wallet },
  { key: 'profile', label: 'Profile', icon: UserRound },
];

const Stat = ({ value, label, tone, testId }) => (
  <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3" data-testid={testId}>
    <div className={`text-3xl font-black leading-none ${tone}`} style={{ fontFamily: 'Barlow Condensed' }}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide font-bold text-white/70 mt-1">{label}</div>
  </div>
);

const NotLinked = ({ user }) => (
  <main className="max-w-2xl mx-auto px-4 py-14" data-testid="portal-not-linked">
    <div className="bg-white border rounded-3xl p-8 text-center">
      <span className="w-14 h-14 mx-auto rounded-2xl bg-[#00247D]/10 text-[#00247D] flex items-center justify-center"><ShieldCheck className="w-6 h-6" /></span>
      <h2 className="mt-4 text-3xl font-black uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>You're not on a league's officials list yet</h2>
      <p className="mt-2 text-sm text-slate-600">Ask your league's assigner for their officials sign-up link, or open the invite email they sent you. Officials never pay.</p>
      {!user?.email_verified && (
        <p className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid="portal-verify-hint">
          <MailWarning className="w-4 h-4" />Verify your email ({user?.email}) so we can match you to leagues that added you.
        </p>
      )}
    </div>
  </main>
);

export default function OfficialsPortal() {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const load = useCallback(() => { oapi('/officials/portal/me').then(setMe).catch(e => toast.error(e.message)); }, []);
  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) { navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`, { replace: true }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated, load]);
  const tab = TABS.some(t => t.key === params.get('tab')) ? params.get('tab') : 'assignments';
  const setTab = (k) => setParams(k === 'assignments' ? {} : { tab: k }, { replace: true });

  if (!me) return <OfficialsLoader />;
  const leagues = me.records.map(r => r.org_name).filter(Boolean);
  const openReqs = me.requests.filter(r => !r.responded);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="officials-portal">
      <OfficialsHeader title="My officiating" subtitle={leagues.join(' · ') || 'Officials portal'} manualAnchor="off-joining">
        <Link to="/officials" className="hidden md:block"><Button variant="ghost" size="sm" className="text-slate-500" data-testid="portal-hub-link">Officials Hub</Button></Link>
      </OfficialsHeader>
      {!me.records.length ? <NotLinked user={user} /> : (
        <>
          <section className="bg-[#00247D] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
              <div className="grid grid-cols-3 gap-3 max-w-xl">
                <Stat value={me.pending.length} label="To answer" tone="text-amber-300" testId="portal-stat-pending" />
                <Stat value={me.upcoming.filter(a => a.status === 'accepted').length} label="Confirmed" tone="text-white" testId="portal-stat-confirmed" />
                <Stat value={me.records.length} label={me.records.length === 1 ? 'League' : 'Leagues'} tone="text-white" testId="portal-stat-leagues" />
              </div>
            </div>
          </section>
          <nav className="bg-white border-b sticky top-16 z-40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
              {TABS.map(t => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? 'border-[#00247D] text-[#00247D]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                  <t.icon className="w-4 h-4 hidden sm:block" />{t.label}
                  {t.key === 'assignments' && me.pending.length > 0 && <span className="ml-0.5 text-[10px] bg-amber-400 text-[#00247D] rounded-full px-1.5 py-0.5 font-bold">{me.pending.length}</span>}
                </button>
              ))}
            </div>
          </nav>
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {openReqs.length > 0 && tab !== 'availability' && (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="portal-requests-banner">
                <BellRing className="w-5 h-5 text-amber-600" />
                <div className="flex-1 min-w-[200px] text-sm text-amber-900">
                  <b>{openReqs[0].org_name}</b> asked for your availability{openReqs[0].title ? `: ${openReqs[0].title}` : ''}{openReqs.length > 1 ? ` (+${openReqs.length - 1} more)` : ''}
                </div>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setTab('availability')} data-testid="portal-requests-go">Update availability</Button>
              </div>
            )}
            {tab === 'assignments' && <PortalAssignments me={me} reload={load} />}
            {tab === 'availability' && <AvailabilityEditor availability={me.availability} onSaved={load} />}
            {tab === 'earnings' && <MyEarnings />}
            {tab === 'profile' && <PortalProfile records={me.records} reload={load} />}
          </main>
        </>
      )}
    </div>
  );
}
